import React, { useCallback, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useCreateTicket, useGetCandidateTickets, useUpdateTicket } from '~/hooks/apis/accommodation/useBundles';

import { BundleOffer, CreateTicketPayload, UpdateTicketPayload } from '~/services/AccommodationService';

import { ALL_CANCELLATION_POLICIES } from '../constants';

type TicketRow = NonNullable<BundleOffer['tickets']>[number];

interface Props {
  bundleId: string;
  offerId: string;
  ticket?: TicketRow;
  open: boolean;
  onClose: () => void;
}

interface OccupancyRow {
  // Maximums; '' (blank) means no upper bound when saved.
  adults: number | '';
  children: number | '';
  infants: number | '';
  // Minimums; '' (blank) means no lower bound (treated as 0) when saved.
  minAdults: number | '';
  minChildren: number | '';
  minInfants: number | '';
}

// Each of the four optional ticket-rule fields uses `null` (or empty array) as the
// "not configured" sentinel. The server treats unconfigured fields as "always passes",
// so omitting them yields a ticket that adds no further constraint on top of the bundle's
// hotel rule.
interface FormState {
  productId: string;
  name: string;
  los: number | '' | null;
  leadDays: number | '' | null;
  cancellationPolicies: Array<'RF' | 'NRF'>;
  includedGuests: Array<OccupancyRow>;
  perOccupant: boolean;
  perNight: boolean;
}

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function buildInitialState(ticket: TicketRow | undefined): FormState {
  if (!ticket) {
    return {
      productId: '',
      name: '',
      los: null,
      leadDays: null,
      cancellationPolicies: [],
      includedGuests: [],
      perOccupant: false,
      perNight: false,
    };
  }
  const capacities: Array<OccupancyRow> = (ticket.includedGuests ?? []).map((c) => ({
    adults: c.adults ?? '',
    children: c.children ?? '',
    infants: c.infants ?? '',
    minAdults: c.minAdults ?? '',
    minChildren: c.minChildren ?? '',
    minInfants: c.minInfants ?? '',
  }));
  return {
    productId: ticket.productId,
    name: ticket.name ?? '',
    los: ticket.los ?? null,
    leadDays: ticket.leadDays ?? null,
    cancellationPolicies: (ticket.cancellationPolicies ?? []) as Array<'RF' | 'NRF'>,
    includedGuests: capacities,
    perOccupant: !!ticket.perOccupant,
    perNight: !!ticket.perNight,
  };
}

function AccommodationBundleTicketDialog({ bundleId, offerId, ticket, open, onClose }: Props) {
  const isEditing = !!ticket;
  const [state, setState] = useState<FormState>(() => buildInitialState(ticket));
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => state.includedGuests.map(allocateRowKey));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createTicketMutation = useCreateTicket();
  const updateTicketMutation = useUpdateTicket();

  const updateOccupancyRow = useCallback((index: number, key: keyof OccupancyRow, value: number | '') => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  }, []);

  const addOccupancyRow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedGuests: [
        ...prev.includedGuests,
        {
          adults: '',
          children: '',
          infants: '',
          minAdults: '',
          minChildren: '',
          minInfants: '',
        },
      ],
    }));
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeOccupancyRow = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.filter((_, i) => i !== index),
    }));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Only load candidate tickets when creating (editing has the productId locked).
  const candidateTicketsQuery = useGetCandidateTickets({
    bundleId,
    offerId,
    enabled: !isEditing && open,
  });

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);

      // Omit unconfigured optional fields entirely so the server's "not configured = passes"
      // semantics kick in. `los`/`leadDays`: null sentinel → undefined. `cancellationPolicies`
      // / `includedGuests`: empty arrays are sent as-is — the server interprets [] as "no constraint".
      // fareType is intentionally not in the payload — it's svc-experience-internal classification,
      // not an admin-overridable field. The server populates `bundle_offer_tickets.fare_type` from
      // the matched candidate via its own svc-experience lookup at addTicket time.
      const payloadBase = {
        name: state.name.trim() || undefined,
        los: typeof state.los === 'number' ? state.los : undefined,
        leadDays: typeof state.leadDays === 'number' ? state.leadDays : undefined,
        cancellationPolicies: state.cancellationPolicies,
        includedGuests: state.includedGuests.map((row) => ({
          // Blank inputs are omitted so absence carries "no bound" semantics:
          // no max => no upper bound, no min => no floor.
          ...(typeof row.adults === 'number' ? { adults: row.adults } : {}),
          ...(typeof row.children === 'number' ? { children: row.children } : {}),
          ...(typeof row.infants === 'number' ? { infants: row.infants } : {}),
          ...(typeof row.minAdults === 'number' ? { minAdults: row.minAdults } : {}),
          ...(typeof row.minChildren === 'number' ? { minChildren: row.minChildren } : {}),
          ...(typeof row.minInfants === 'number' ? { minInfants: row.minInfants } : {}),
        })),
        perOccupant: state.perOccupant,
        perNight: state.perNight,
      };

      try {
        if (isEditing && ticket) {
          const payload: UpdateTicketPayload = payloadBase;
          await updateTicketMutation.mutateAsync({
            bundleId,
            offerId,
            ticketId: ticket.id,
            payload,
          });
        } else {
          const payload: CreateTicketPayload = {
            ...payloadBase,
            productId: state.productId.trim(),
          };
          await createTicketMutation.mutateAsync({ bundleId, offerId, payload });
        }
        onClose();
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save ticket');
      }
    },
    [isEditing, ticket, state, createTicketMutation, updateTicketMutation, bundleId, offerId, onClose],
  );

  const candidateTickets = candidateTicketsQuery.data?.tickets ?? [];
  const selectedCandidate = useMemo(
    () => candidateTickets.find((t) => t.productId === state.productId),
    [candidateTickets, state.productId],
  );

  const isSubmitting = createTicketMutation.isPending || updateTicketMutation.isPending;
  const canSubmit = !!state.productId && !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEditing ? 'Edit Ticket' : 'Add Ticket'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {!isEditing && (
              <Box>
                <Typography variant="subtitle2" mb={1}>
                  Pick a candidate ticket from svc-experience
                </Typography>
                {candidateTicketsQuery.isLoading ? (
                  <Spinner />
                ) : candidateTicketsQuery.error ? (
                  <Alert severity="error">
                    Failed to load candidate tickets: {candidateTicketsQuery.error.message}
                  </Alert>
                ) : candidateTickets.length === 0 ? (
                  <Alert severity="info">No candidate tickets available from svc-experience for this experience.</Alert>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Product</InputLabel>
                    <Select
                      label="Product"
                      value={state.productId}
                      onChange={(e) => {
                        const productId = e.target.value as string;
                        const candidate = candidateTickets.find((t) => t.productId === productId);
                        setState((prev) => ({
                          ...prev,
                          productId,
                          name: candidate?.name ?? prev.name,
                        }));
                      }}
                      required
                    >
                      {candidateTickets.map((t) => (
                        <MenuItem key={t.productId} value={t.productId}>
                          {t.name ?? t.fareType ?? t.productId}{' '}
                          {t.price != null && t.currency ? `— ${t.price} ${t.currency}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {selectedCandidate && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Selected: {selectedCandidate.name ?? selectedCandidate.fareType ?? selectedCandidate.productId}
                    {selectedCandidate.price != null && selectedCandidate.currency
                      ? ` — ${selectedCandidate.price} ${selectedCandidate.currency}`
                      : ''}
                  </Typography>
                )}
              </Box>
            )}

            {isEditing && <TextField label="Product ID" value={state.productId} disabled fullWidth />}

            <TextField
              label="Name (display label, optional)"
              value={state.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
            />

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Optional ticket rules — leave any field unconfigured to skip that constraint. The bundle's hotel rule
                still applies regardless.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                {state.los === null ? (
                  <Button onClick={() => handleChange('los', 1)} variant="outlined" fullWidth>
                    + Add Min length of stay
                  </Button>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      label="Min length of stay (nights)"
                      type="number"
                      value={state.los}
                      onChange={(e) => handleChange('los', e.target.value === '' ? '' : Number(e.target.value))}
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => handleChange('los', null)}
                      size="small"
                      aria-label="Remove min length of stay"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}>
                {state.leadDays === null ? (
                  <Button onClick={() => handleChange('leadDays', 0)} variant="outlined" fullWidth>
                    + Add Min lead days
                  </Button>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      label="Min lead days"
                      type="number"
                      value={state.leadDays}
                      onChange={(e) => handleChange('leadDays', e.target.value === '' ? '' : Number(e.target.value))}
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => handleChange('leadDays', null)}
                      size="small"
                      aria-label="Remove min lead days"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                {state.cancellationPolicies.length === 0 ? (
                  <Button
                    onClick={() => handleChange('cancellationPolicies', ['RF', 'NRF'])}
                    variant="outlined"
                    fullWidth
                  >
                    + Add Cancellation policies constraint
                  </Button>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormControl fullWidth>
                      <InputLabel>Cancellation Policies</InputLabel>
                      <Select
                        multiple
                        label="Cancellation Policies"
                        value={state.cancellationPolicies}
                        onChange={(e) => handleChange('cancellationPolicies', e.target.value as Array<'RF' | 'NRF'>)}
                      >
                        {ALL_CANCELLATION_POLICIES.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => handleChange('cancellationPolicies', [])}
                      size="small"
                      aria-label="Remove cancellation policies"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                {state.includedGuests.length === 0 ? (
                  <Button
                    onClick={() => {
                      setState((prev) => ({
                        ...prev,
                        includedGuests: [
                          {
                            adults: '',
                            children: '',
                            infants: '',
                            minAdults: '',
                            minChildren: '',
                            minInfants: '',
                          },
                        ],
                      }));
                      setRowKeys((prev) => [...prev, allocateRowKey()]);
                    }}
                    variant="outlined"
                    fullWidth
                  >
                    + Add Occupancy
                  </Button>
                ) : (
                  <>
                    <Typography variant="subtitle2" mb={1}>
                      Occupancies
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      A rate must satisfy at least one of these rows to be eligible for this ticket. Each row sets
                      optional MIN and/or MAX per guest type independently — leave a field empty to omit that bound (no
                      minimum floor, or no maximum ceiling).
                    </Typography>
                    <Stack spacing={2}>
                      {state.includedGuests.map((row, index) => (
                        <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2">Occupancy #{index + 1}</Typography>
                            <IconButton
                              onClick={() => removeOccupancyRow(index)}
                              size="small"
                              aria-label="Remove occupancy row"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Stack spacing={2}>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <TextField
                                  label="Min Adults"
                                  type="number"
                                  value={row.minAdults}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'minAdults',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no minimum"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  label="Min Children"
                                  type="number"
                                  value={row.minChildren}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'minChildren',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no minimum"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  label="Min Infants"
                                  type="number"
                                  value={row.minInfants}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'minInfants',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no minimum"
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <TextField
                                  label="Max Adults"
                                  type="number"
                                  value={row.adults}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'adults',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no maximum"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  label="Max Children"
                                  type="number"
                                  value={row.children}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'children',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no maximum"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  label="Max Infants"
                                  type="number"
                                  value={row.infants}
                                  onChange={(e) =>
                                    updateOccupancyRow(
                                      index,
                                      'infants',
                                      e.target.value === '' ? '' : Number(e.target.value),
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  helperText="Leave blank for no maximum"
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                    <Box mt={2}>
                      <Button onClick={addOccupancyRow} variant="outlined">
                        + Add occupancy
                      </Button>
                    </Box>
                  </>
                )}
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.perOccupant}
                      onChange={(e) => handleChange('perOccupant', e.target.checked)}
                    />
                  }
                  label="Per Occupant"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch checked={state.perNight} onChange={(e) => handleChange('perNight', e.target.checked)} />
                  }
                  label="Per Night"
                />
              </Grid>
            </Grid>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleTicketDialog;
